import {
  getCheckpointVersion,
  normalizeCheckpointId,
  normalizeCheckpointVersion,
} from '@/lib/checkpointKeys';
import { titleToKey } from '@/lib/utils';
import { resolveAccountableJiraBoard } from '@/domains/access/systemConfigApi';
import type {
  AccountableJiraBoards,
  CheckpointDivision,
  CheckpointGroup,
  CheckpointItem,
  Evaluation,
  EvaluationCheckpoint,
} from '@/types';

export const MOBILE_MEDIA_QUERY = '(max-width: 47.9375rem)';
export const ONBOARDING_EXCEPTION_SUB_CATEGORY = 'orr onboarding';
export const ONBOARDING_EXCEPTION_LABEL = 'ONBOARDING EXCEPTION';

export const isCheckpointGroup = (
  checkpoint: CheckpointItem,
): checkpoint is CheckpointGroup => checkpoint.type === 'group';

export const isCheckpointItem = (
  checkpoint: CheckpointItem,
): checkpoint is EvaluationCheckpoint => checkpoint.type === 'item';

export const getCheckpointKey = (checkpoint: EvaluationCheckpoint) => {
  const version = getCheckpointVersion(checkpoint.metadata);
  return `${checkpoint.metadata.id}::${version ?? ''}`;
};

export const getCheckpointDomKey = (checkpoint: EvaluationCheckpoint) =>
  getCheckpointKey(checkpoint).replace(/[^a-zA-Z0-9_-]/g, '-');

export const getExceptionLabel = (checkpoint: EvaluationCheckpoint) =>
  checkpoint.exceptionDetails?.exception_sub_category?.trim().toLowerCase() ===
  ONBOARDING_EXCEPTION_SUB_CATEGORY
    ? ONBOARDING_EXCEPTION_LABEL
    : 'EXCEPTION';

export const shouldStackCheckpointStatusTags = (
  checkpoint: EvaluationCheckpoint,
) => getExceptionLabel(checkpoint) === ONBOARDING_EXCEPTION_LABEL;

export const buildVersionsByCheckpointId = (evaluation?: Evaluation | null) => {
  const map = new Map<string, Set<string>>();
  const checkpoints = evaluation?.checkpoints ?? [];

  for (const checkpoint of checkpoints) {
    const idKey = normalizeCheckpointId(checkpoint.metadata.id);
    if (!idKey) continue;

    const version = getCheckpointVersion(checkpoint.metadata);
    if (!version) continue;

    const normalizedVersion = normalizeCheckpointVersion(version);
    if (!normalizedVersion) continue;

    const set = map.get(idKey) ?? new Set<string>();
    set.add(normalizedVersion);
    map.set(idKey, set);
  }

  return map;
};

export const computeCheckpointsByAccountable = (
  evaluation: Evaluation,
  // Accountable-team -> Jira board map is data-driven (GET /system/jira-boards),
  // shared with Report-a-Bug — never hardcoded here.
  accountableJiraBoards?: AccountableJiraBoards,
) => {
  return evaluation.checkpoints.reduce(
    (acc, checkpoint) => {
      const accountable = checkpoint.metadata.accountable;
      const accountableKey = titleToKey(accountable);

      if (!acc[accountableKey]) {
        acc[accountableKey] = {
          title: accountable,
          checkpoints: [],
          isActive: false,
          bugKey:
            resolveAccountableJiraBoard(accountableJiraBoards, accountable) ??
            '',
        };
      }

      if ('group' in checkpoint.metadata && checkpoint.metadata.group) {
        const existingGroup = acc[accountableKey].checkpoints.find(
          (candidate): candidate is CheckpointGroup =>
            'group' in candidate && candidate.group === checkpoint.metadata.group,
        );

        if (existingGroup) {
          existingGroup.checkpoints.push({
            ...checkpoint,
            type: 'item',
          } as EvaluationCheckpoint);
        } else {
          acc[accountableKey].checkpoints.push({
            type: 'group',
            isActive: false,
            group: checkpoint.metadata.group,
            groupName: checkpoint.metadata.group,
            checkpoints: [
              {
                ...checkpoint,
                type: 'item',
              } as EvaluationCheckpoint,
            ],
          } as CheckpointGroup);
        }
      } else {
        acc[accountableKey].checkpoints.push({
          ...checkpoint,
          type: 'item',
        } as EvaluationCheckpoint);
      }

      return acc;
    },
    {} as Record<string, CheckpointDivision>,
  );
};

export const getCheckpointCount = (checkpointDivision: CheckpointDivision) => {
  let count = 0;

  checkpointDivision.checkpoints.forEach((checkpoint) => {
    if (isCheckpointGroup(checkpoint)) {
      count += checkpoint.checkpoints.length;
    } else {
      count += 1;
    }
  });

  return count;
};
